'use client'

import React, { useEffect, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertCircle, ArrowUpRight, Crown, Zap } from 'lucide-react'
import { useRouter } from 'next/navigation'

export function SubscriptionLimitModal() {
    const router = useRouter()
    const [isOpen, setIsOpen] = useState(false)
    const [errorDetails, setErrorDetails] = useState<any>(null)

    useEffect(() => {
        const handleLimitExceeded = (event: any) => {
            setErrorDetails(event.detail)
            setIsOpen(true)
        }

        window.addEventListener('subscription_limit_exceeded', handleLimitExceeded)
        return () => window.removeEventListener('subscription_limit_exceeded', handleLimitExceeded)
    }, [])

    const handleUpgrade = () => {
        setIsOpen(false)
        router.push('/subscription-plans')
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="sm:max-w-[480px] border-none shadow-2xl overflow-hidden p-0">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-destructive" />

                <div className="p-8">
                    <div className="flex justify-center mb-6">
                        <div className="h-20 w-20 rounded-full bg-destructive/10 flex items-center justify-center relative">
                            <Crown className="h-10 w-10 text-destructive" />
                            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm border">
                                <AlertCircle className="h-6 w-6 text-destructive animate-pulse" />
                            </div>
                        </div>
                    </div>

                    <DialogHeader className="text-center space-y-3">
                        <DialogTitle className="text-2xl font-bold text-foreground">
                            Plan Limit Reached
                        </DialogTitle>
                        <DialogDescription className="text-base text-foreground/80">
                            {errorDetails?.message?.description || "You've reached the maximum number of items allowed on your current plan."}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-8 bg-muted/30 rounded-xl p-5 border border-border/50">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 p-2 rounded-lg bg-primary/10">
                                <Zap className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-foreground">Go Professional</h4>
                                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                    Upgrade to Professional for unlimited Institutes, Classes, and advanced AI-powered visualizations and chart types.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col gap-3">
                        <Button
                            className="w-full py-6 text-lg font-bold shadow-lg shadow-primary/20"
                            onClick={handleUpgrade}
                            variant="professional"
                        >
                            View Subscription Plans
                            <ArrowUpRight className="ml-2 h-5 w-5" />
                        </Button>
                        <Button
                            variant="ghost"
                            className="w-full text-muted-foreground hover:text-foreground"
                            onClick={() => setIsOpen(false)}
                        >
                            Close
                        </Button>
                    </div>
                </div>

                <div className="px-8 py-3 bg-muted/50 border-t flex justify-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium">
                        ExpertBridge Professional Account Required
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    )
}
